import { z } from "zod";

export enum CrmProvider {
	HUBSPOT = "hubspot",
	ZOHO = "zoho",
	PIPEDRIVE = "pipedrive",
	MSDYNAMICS = "msdynamics",
	SALESFORCE = "salesforce",
}

export enum CrmEntityType {
	CONTACT = "contact",
	ACCOUNT = "account",
	LEAD = "lead",
	DEAL = "deal",
}

export enum CrmEntityStatus {
	PENDING = "PENDING",
	COMPLETED = "COMPLETED",
	FAILED = "FAILED",
}

export enum CrmSelectionSource {
	CONTACTS = "contacts",
	LIST = "list",
}

export const AddToCrmFormSchema = z.object({
	contact: z.boolean(),
	account: z.boolean(),
	lead: z.boolean(),
	deal: z.boolean(),
});

export type AddToCrmFormValues = z.infer<typeof AddToCrmFormSchema>;

export const AddToCrmSelectionSchema = z.object({
	source: z.nativeEnum(CrmSelectionSource),
	selectType: z.enum(["CURRENT_PAGE", "ALL"]),
	ids: z.array(z.number()).optional(),
	listPersonIds: z.array(z.number()).optional(),
	listId: z.number().optional(),
	filter: z.union([z.string(), z.record(z.string(), z.unknown())]).optional(),
	searchValue: z.string().optional(),
	semanticSearchProspects: z.string().optional(),
	semanticSearchOrganizations: z.string().optional(),
	assignStatus: z.array(z.string()).optional(),
	inclusionFilterStatus: z.array(z.string()).optional(),
	priorityFilterStatus: z.array(z.string()).optional(),
	signalFilterStatus: z.array(z.string()).optional(),
	/** The table's reason sub-filters, "STATUS:filterParam" (PREC-4450). */
	exclusionReasons: z.array(z.string()).optional(),
});

export const AddProspectsToCrmRequestSchema = z.object({
	provider: z.nativeEnum(CrmProvider),
	entities: z.array(z.nativeEnum(CrmEntityType)).min(1),
	selection: AddToCrmSelectionSchema,
	ownerId: z.string().optional(),
	ownerName: z.string().optional(),
});

export const AddProspectsToCrmResponseSchema = z.object({
	success: z.boolean().optional(),
	provider: z.nativeEnum(CrmProvider).optional(),
	entities: z.array(z.nativeEnum(CrmEntityType)).optional(),
	jobId: z.union([z.string(), z.number()]).optional(),
	queued: z.number().optional(),
	skipped: z.number().optional(),
	message: z.string().optional(),
});

const CrmRealtimeRecordSchema = z
	.object({
		id: z.number().nullable().optional(),
		crmType: z.string().nullable().optional(),
		syncStatus: z.string().nullable().optional(),
		syncError: z.string().nullable().optional(),
	})
	.passthrough();

export const CrmProspectUpdateSchema = z.object({
	prospectId: z.number(),
	companyId: z.number(),
	provider: z.nativeEnum(CrmProvider),
	entity: z.nativeEnum(CrmEntityType),
	status: z.nativeEnum(CrmEntityStatus),
	crmProspect: CrmRealtimeRecordSchema.nullable().optional(),
	crmOrganization: CrmRealtimeRecordSchema.nullable().optional(),
	crmDeal: CrmRealtimeRecordSchema.nullable().optional(),
	crmLead: CrmRealtimeRecordSchema.nullable().optional(),
	error: z.string().optional(),
});

export type AddToCrmSelection = z.infer<typeof AddToCrmSelectionSchema>;
export type AddProspectsToCrmRequest = z.infer<typeof AddProspectsToCrmRequestSchema>;
export type AddProspectsToCrmResponse = z.infer<typeof AddProspectsToCrmResponseSchema>;
export type CrmRealtimeRecord = z.infer<typeof CrmRealtimeRecordSchema>;
export type CrmProspectUpdate = z.infer<typeof CrmProspectUpdateSchema>;
